import React, { useState, useEffect } from 'react';
import { Card, Grid, CircularProgress, Alert } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { getPlatformStats } from 'services/statsService';

const LABEL_MAP = {
  'id-scans': 'ID Scans',
  'credit-pull': 'Credit Pulls',
  'dmv': 'DMV',
  'id_visual_analysis': 'ID Visual Analysis',
  'business_verification': 'Business Verification',
  'email_verification': 'Email Verification',
  'phone_verification': 'Phone Verification',
  'ip_verification': 'IP Verification',
  'fraud_list_check': 'Fraud List Check',
  'credit_fraud_alerts': 'Credit Fraud Alerts',
};

const COLORS = [
  '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
  '#0097a7', '#c2185b', '#455a64', '#fbc02d', '#5d4037',
];

function PlatformDashboard() {
  const [stats, setStats] = useState(null);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await getPlatformStats();
      setStats(data.stats || []);
      setMonths(data.months || []);
    } catch (err) {
      console.error('Error loading platform stats:', err);
      setError('Failed to load platform stats');
    } finally {
      setLoading(false);
    }
  };

  const chartData = months.slice(-12).map((month) => {
    const point = { month };
    (stats || []).forEach((s) => {
      point[s.type] = s.monthly[month] || 0;
    });
    return point;
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {loading ? (
          <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
          </SoftBox>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <SoftBox mb={3}>
              <SoftTypography variant="h5" fontWeight="medium" mb={2}>
                Lifetime Totals
              </SoftTypography>
              <Grid container spacing={2}>
                {stats.map((s) => (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={s.type}>
                    <Card>
                      <SoftBox p={2} textAlign="center">
                        <SoftTypography variant="caption" color="text" fontWeight="medium">
                          {LABEL_MAP[s.type] || s.type}
                        </SoftTypography>
                        <SoftTypography variant="h4" fontWeight="bold">
                          {s.lifetime.toLocaleString()}
                        </SoftTypography>
                      </SoftBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </SoftBox>

            <SoftBox mb={3}>
              <Card>
                <SoftBox p={3}>
                  <SoftTypography variant="h5" fontWeight="medium" mb={2}>
                    Monthly Trends
                  </SoftTypography>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {stats.map((s, i) => (
                        <Line
                          key={s.type}
                          type="monotone"
                          dataKey={s.type}
                          name={LABEL_MAP[s.type] || s.type}
                          stroke={COLORS[i % COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </SoftBox>
              </Card>
            </SoftBox>
          </>
        )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PlatformDashboard;
